import classNames from 'classnames';
import _ from 'lodash';
import type { ReactNode } from 'react';
import React, { useContext, useMemo } from 'react';
import SVGIcon from '../../components/SVGIcon';
import PopOver from '../../components/deprecated/PopOver';
import addTestId from '../../utils/addTestId';
import truncate from '../../utils/text/truncate';
import safeParseUrl from '../../utils/url/safeParseUrl';
import truncateUrl from '../../utils/url/truncateUrl';
import { isOrganizationOrigin } from '../utils/matchCurrentUrl';
import styles from './MentionBase.module.scss';
import Link from '../../utils/router/Link';

export const TRUNCATION_LENGTHS = {
  comment: 20,
  activity: 15,
  editor: 35,
} as const;

export type ContainerType = keyof typeof TRUNCATION_LENGTHS;

const DEFAULT_TRUNCATE_LENGTH = 35;
const URL_TRUNCATION_ENDING_LENGTH = 5;

export const shouldTruncate = (type: string) => {
  return type !== 'user';
};

const truncateMentionContent = (content: string, maxLength: number) => {
  const isUrl = content.startsWith('http');

  if (isUrl) {
    return truncateUrl(content, maxLength, URL_TRUNCATION_ENDING_LENGTH);
  }

  return truncate(content, maxLength);
};

const MentionBaseContext = React.createContext({
  truncateLength: DEFAULT_TRUNCATE_LENGTH,
});

interface MentionBaseProviderProps {
  children: ReactNode;
  containerType: ContainerType;
}

export const MentionBaseProvider = ({
  children,
  containerType,
}: MentionBaseProviderProps) => {
  const value = useMemo(
    () => ({
      truncateLength: TRUNCATION_LENGTHS[containerType],
    }),
    [containerType],
  );

  return (
    <MentionBaseContext.Provider value={value}>
      {children}
    </MentionBaseContext.Provider>
  );
};

export const MENTION_CONTAINER_CLASS = styles.container;

type MentionBaseProps = {
  type: string;
  to?: string;
  content?: string;
  popOver?: ReactNode;
  popOverProps?: Record<string, unknown>;
  icon?: ReactNode | string;
  iconAfter?: ReactNode;
  iconClassName?: string;
  contentClassName?: string;
  closed?: boolean;
  singleLine?: boolean;
  newTab?: boolean;
  iconLoading?: boolean;
  innerHeadRef?: React.Ref<HTMLSpanElement>;
};

const MentionBase = ({
  type,
  to,
  newTab = false,
  content = '',
  popOver,
  popOverProps = {},
  icon,
  iconAfter,
  iconClassName,
  contentClassName,
  closed = false,
  singleLine,
  iconLoading,
  innerHeadRef,
}: MentionBaseProps) => {
  const { truncateLength } = useContext(MentionBaseContext);
  const chars = _.split(
    shouldTruncate(type) && !singleLine
      ? truncateMentionContent(content, truncateLength)
      : content,
    '',
  );
  const firstLetter = chars.shift();
  const lastLetter = chars.pop();
  const middleContent = chars.join('');

  const wrapIcon = (inner: string | ReactNode) =>
    (inner || iconLoading) && (
      <span
        data-no-print
        className={classNames(styles.icon, iconClassName, {
          [styles.isLoading]: iconLoading,
        })}
      >
        {typeof inner === 'string' ? <SVGIcon inline>{inner}</SVGIcon> : inner}
      </span>
    );

  const finalContentClassName =
    classNames(contentClassName, {
      [styles.closed]: closed,
    }) || undefined;

  const textWrappingGroups = lastLetter ? (
    <>
      {singleLine ? (
        <>
          <span
            className={classNames(
              styles.innerHead,
              styles.singleLine,
              finalContentClassName,
            )}
            ref={innerHeadRef}
          >
            {wrapIcon(icon)}
            {firstLetter}
            {middleContent}
            {lastLetter}
          </span>
          <span className={styles.innerTail}>{wrapIcon(iconAfter)}</span>
        </>
      ) : (
        <>
          <span
            className={classNames(styles.innerHead, finalContentClassName)}
            ref={innerHeadRef}
          >
            {wrapIcon(icon)}
            {firstLetter}
          </span>
          <span className={finalContentClassName}>{middleContent}</span>
          <span className={classNames(styles.innerTail, finalContentClassName)}>
            {lastLetter}
            {wrapIcon(iconAfter)}
          </span>
        </>
      )}
    </>
  ) : (
    <span
      ref={innerHeadRef}
      className={classNames(
        styles.innerHead,
        styles.innerTail,
        finalContentClassName,
      )}
    >
      {wrapIcon(icon)}
      {firstLetter}
      {wrapIcon(iconAfter)}
    </span>
  );

  let mentionInner;

  const innerClassNames = classNames(styles.inner, {
    [styles.singleLine]: singleLine,
  });

  if (to) {
    const toUrl = safeParseUrl(to, window.location.href);
    const shouldOpenInNewTab = newTab || !toUrl || !isOrganizationOrigin(toUrl);

    mentionInner = shouldOpenInNewTab ? (
      <Link
        className={innerClassNames}
        to={to}
        target="_blank"
        rel="nofollow noopener noreferrer ugc"
      >
        {textWrappingGroups}
      </Link>
    ) : (
      <Link className={innerClassNames} to={to}>
        {textWrappingGroups}
      </Link>
    );
  } else {
    mentionInner = (
      <span className={innerClassNames}>{textWrappingGroups}</span>
    );
  }

  const mentionPillWithMargin = (
    <>
      <span className={styles.margin} />
      <span
        className={classNames(MENTION_CONTAINER_CLASS, styles[type])}
        {...addTestId('mentionContainer')}
      >
        {mentionInner}
      </span>
      <span className={styles.margin} />
    </>
  );

  return popOver ? (
    <PopOver
      display="inline"
      placement="top-start"
      popOver={popOver}
      trigger="hover"
      popOverPortal={document.body}
      {...popOverProps}
    >
      {mentionPillWithMargin}
    </PopOver>
  ) : (
    mentionPillWithMargin
  );
};

export default MentionBase;
